import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { vendorApi } from '../../services/api';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import { format } from 'date-fns';
import { VENDOR_STATUS_OPTIONS as STATUSES } from '../../utils/statusLabels';

// ── Reusable sortable header cell ────────────────────────────────────
function SortTh({ col, label, sortCol, sortDir, onSort }) {
  const active = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-800 hover:bg-gray-100 transition-colors"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active
          ? <span className="text-blue-500">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>
          : <span className="text-gray-300"> ⇅</span>}
      </span>
    </th>
  );
}

export default function VendorAssignments() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [page, setPage]         = useState(0);
  const [status, setStatus]     = useState(searchParams.get('status') || '');
  const [fgSearch, setFgSearch] = useState('');
  const [fgCode, setFgCode]     = useState('');
  const [factoryCodeSearch, setFactoryCodeSearch] = useState('');
  const [factoryCode, setFactoryCode] = useState('');
  const [factoryNameSearch, setFactoryNameSearch] = useState('');
  const [factoryName, setFactoryName] = useState('');
  const [sortCol, setSortCol]   = useState('assignedAt');
  const [sortDir, setSortDir]   = useState('desc');

  // Debounce the search input — fire API only after 400 ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setFgCode(fgSearch.trim());
      setFactoryCode(factoryCodeSearch.trim());
      setFactoryName(factoryNameSearch.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [fgSearch, factoryCodeSearch, factoryNameSearch]);

  const { data, isLoading } = useQuery(
    ['vendor-assignments', page, status, fgCode, factoryCode, factoryName, sortCol, sortDir],
    () => vendorApi.getAssignments({
      page,
      size: 15,
      status: status && status !== 'REJECTED' ? status : undefined,
      rejected: status === 'REJECTED' ? true : undefined,
      fgCode: fgCode || undefined,
      factoryCode: factoryCode || undefined,
      factoryName: factoryName || undefined,
      sort: `${sortCol},${sortDir}`,
    }),
    { keepPreviousData: true }
  );

  const content    = data?.data?.content || [];
  const totalPages = data?.data?.totalPages || 0;

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    setPage(0);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Vendor Assignments</h1>
        <p className="text-sm text-gray-500 mt-0.5">Finished good requests assigned to your vendor company</p>
      </div>

      {/* Filter & Search */}
      <div className="flex gap-3 flex-wrap">
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>

        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by FG Code…"
            value={fgSearch}
            onChange={(e) => setFgSearch(e.target.value)}
            className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-52"
          />
          {fgSearch && (
            <button
              onClick={() => setFgSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
        <input
          type="text"
          placeholder="Filter Factory Code"
          value={factoryCodeSearch}
          onChange={(e) => setFactoryCodeSearch(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
        />
        <input
          type="text"
          placeholder="Filter Factory Name"
          value={factoryNameSearch}
          onChange={(e) => setFactoryNameSearch(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-52"
        />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
        {isLoading ? <LoadingSpinner /> : (
          <>
          <table className="w-full min-w-[1120px] text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <SortTh col="bobFinishedGoodCode"      label="FG Code"        sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="collectionName"            label="Collection"     sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="topCategory"               label="Top Category"   sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="category"                  label="Category"       sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="vendorFinishedGoodModel"   label="Vendor FG Model" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="factoryCode"               label="Factory Code"   sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="factoryName"               label="Factory Name"   sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="status"                    label="Status"         sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="assignedAt"                label="Assigned"       sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="updatedAt"                 label="Last Updated"   sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {content.length === 0 ? (
                  <tr><td colSpan={11} className="px-4 py-12 text-center text-gray-400">No assignments found</td></tr>
                ) : content.map((a) => {
                  const daysSinceAssigned = a.assignedAt
                    ? Math.floor((Date.now() - new Date(a.assignedAt)) / 86_400_000)
                    : 0;
                  const overdueRow = a.status === 'ASSIGNED' && daysSinceAssigned >= 3;
                  return (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs">
                      <div className="relative group inline-block">
                        <span className={overdueRow ? 'text-red-600 font-bold' : 'text-orange-600 font-medium'}>
                          {a.bobFinishedGoodCode || '—'}
                        </span>
                        {overdueRow && (
                          <span className="absolute bottom-full left-0 mb-1.5 hidden group-hover:block bg-red-700 text-white text-xs font-normal rounded-md px-2.5 py-1.5 whitespace-nowrap z-20 shadow-lg pointer-events-none">
                            ⚠ No submission since assignment — {daysSinceAssigned} day{daysSinceAssigned !== 1 ? 's' : ''} ago
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-36 truncate">{a.collectionName || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{a.topCategory || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{a.category || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{a.vendorFinishedGoodModel || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">{a.factoryCode || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 max-w-40 truncate" title={a.factoryName}>{a.factoryName || '—'}</td>
                    <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                    <td className="px-4 py-3 text-xs text-gray-400">{a.assignedAt ? format(new Date(a.assignedAt), 'dd MMM yyyy') : '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-400">{a.updatedAt ? format(new Date(a.updatedAt), 'dd MMM yyyy, HH:mm') : '—'}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => navigate(`/vendor/assignments/${a.id}`)}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800">
                        View →
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}
